import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { ErrorBanner, Loading, Notice } from "@/components/ui/feedback";
import { Toaster } from "@/components/ui/sonner";
import { useLogout, useMe, useWorkspaces } from "../api/session";
import { AssistantProvider } from "../features/assistant/AssistantProvider";
import { useBreakpoint } from "../lib/breakpoint";
import { can } from "../lib/permissions";
import { OfflineBanner } from "../pwa/OfflineBanner";
import { BottomNav } from "./BottomNav";
import { NewEntryProvider } from "./NewEntry";
import { QuickAddProvider } from "./QuickAdd";
import { SideNav } from "./SideNav";
import { TopBar } from "./TopBar";
import { rememberWorkspace, WorkspaceContext } from "./workspace-context";

export function WorkspaceLayout() {
  const { wid } = useParams();
  const workspaces = useWorkspaces();
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const workspace = workspaces.data?.find((w) => w.id === wid);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    if (workspace) rememberWorkspace(workspace.id);
  }, [workspace]);

  if (workspaces.isPending) return <Loading />;
  if (workspaces.isError) return <ErrorBanner error={workspaces.error} />;
  if (!workspace) {
    const first = workspaces.data[0];
    return first ? (
      <Navigate to={`/w/${first.id}`} replace state={{ notice: "Você não tem acesso àquele workspace." }} />
    ) : (
      <Navigate to="/" replace />
    );
  }

  async function signOut() {
    await logout.mutateAsync().catch(() => undefined);
    navigate("/entrar", { replace: true });
  }

  const canWrite = can(workspace.role, "transaction:write");
  const mobile = breakpoint === "mobile";

  return (
    <WorkspaceContext.Provider value={workspace}>
      {/* key: trocar de workspace remonta o provider e apaga a conversa. Ele fica por fora: a escolha do
          "Novo lançamento" precisa saber se o assistente está liberado. */}
      <AssistantProvider key={workspace.id} enabled={me.data?.assistantEnabled ?? false}>
        <QuickAddProvider>
          <NewEntryProvider>
            <div className="flex min-h-dvh bg-bg text-ink">
              {!mobile && (
                <SideNav
                  workspace={workspace}
                  workspaces={workspaces.data}
                  email={me.data?.email}
                  platformAdmin={me.data?.isPlatformAdmin ?? false}
                  collapsed={breakpoint === "tablet"}
                  onSignOut={() => void signOut()}
                />
              )}
              <div
                className={
                  mobile
                    ? "flex min-w-0 flex-1 flex-col"
                    : "mx-auto flex w-full max-w-6xl min-w-0 flex-1 flex-col px-8 pt-6"
                }
              >
                <TopBar
                  mobile={mobile}
                  compactNav={breakpoint === "tablet"}
                  workspace={workspace}
                  workspaces={workspaces.data}
                  canWrite={canWrite}
                />
                <main className={mobile ? "flex-1 px-4 pt-2 pb-32" : "flex-1 pt-2 pb-12"}>
                  <OfflineBanner />
                  {notice && (
                    <div className="mb-4">
                      <Notice>{notice}</Notice>
                    </div>
                  )}
                  <Outlet />
                </main>
              </div>
              {mobile && (
                <BottomNav
                  canWrite={canWrite}
                  email={me.data?.email}
                  platformAdmin={me.data?.isPlatformAdmin ?? false}
                  onSignOut={() => void signOut()}
                />
              )}
            </div>
          </NewEntryProvider>
        </QuickAddProvider>
      </AssistantProvider>
      <Toaster />
    </WorkspaceContext.Provider>
  );
}
